import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import {
  doc, getDoc, collection, getDocs,
  addDoc, updateDoc, query, orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import EmptyState from '../../components/ui/EmptyState';
import { toast } from '../../components/ui/Toast';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconArrowLeft,
  IconDroplet,
  IconPlus,
  IconCheck,
  IconAlertTriangle,
  IconTractor,
  IconClock,
} from '@tabler/icons-react';

export default function DervDetail() {
  const { language } = useTranslation();
  const { tankId } = useParams();
  const navigate = useNavigate();
  const { orgId, userProfile } = useAuth();

  const [tank, setTank] = useState(null);
  const [fills, setFills] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddFill, setShowAddFill] = useState(false);
  const [saving, setSaving] = useState(false);

  // Fill form
  const [litresAdded, setLitresAdded] = useState('');
  const [machineId, setMachineId] = useState('');
  const [cost, setCost] = useState('');
  const [fillNote, setFillNote] = useState('');

  useEffect(() => {
    const load = async () => {
      if (!orgId || !tankId) return;
      try {
        const [tankSnap, fillsSnap, machinesSnap] = await Promise.all([
          getDoc(doc(db, 'organisations', orgId, 'dervTanks', tankId)),
          getDocs(query(
            collection(db, 'organisations', orgId, 'dervTanks', tankId, 'fills'),
            orderBy('timestamp', 'desc')
          )),
          getDocs(collection(db, 'organisations', orgId, 'machines')),
        ]);
        if (tankSnap.exists()) setTank({ id: tankSnap.id, ...tankSnap.data() });
        setFills(fillsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setMachines(machinesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [orgId, tankId]);

  const handleAddFill = async () => {
    if (!litresAdded || Number(litresAdded) <= 0) {
      toast.error('Please enter litres added');
      return;
    }
    const litres = Number(litresAdded);
    const newLevel = (tank.currentLitres || 0) + litres;
    if (newLevel > tank.capacityLitres) {
      toast.error(`This would exceed tank capacity of ${tank.capacityLitres.toLocaleString()}L`);
      return;
    }

    setSaving(true);
    try {
      const machine = machines.find(m => m.id === machineId);
      const fill = {
        litresAdded: litres,
        litresBefore: tank.currentLitres || 0,
        litresAfter: newLevel,
        machineId: machineId || null,
        machineName: machine ? `${machine.makeLabel} ${machine.modelLabel}` : null,
        cost: cost ? Number(cost) : null,
        note: fillNote.trim() || null,
        addedBy: userProfile?.id,
        addedByName: userProfile?.name,
        timestamp: serverTimestamp(),
      };

      const fillRef = await addDoc(
        collection(db, 'organisations', orgId, 'dervTanks', tankId, 'fills'),
        fill
      );

      // Update tank level
      await updateDoc(doc(db, 'organisations', orgId, 'dervTanks', tankId), {
        currentLitres: newLevel,
        updatedAt: serverTimestamp(),
      });

      // Update machine fuel cost if applicable
      if (machineId && cost) {
        const machineRef = doc(db, 'organisations', orgId, 'machines', machineId);
        const machineSnap = await getDoc(machineRef);
        if (machineSnap.exists()) {
          const currentFuelCost = machineSnap.data().costTracking?.totalFuelCost || 0;
          await updateDoc(machineRef, {
            'costTracking.totalFuelCost': currentFuelCost + Number(cost),
            updatedAt: serverTimestamp(),
          });
        }
      }

      setTank(prev => ({ ...prev, currentLitres: newLevel }));
      setFills(prev => [{ id: fillRef.id, ...fill, timestamp: { toDate: () => new Date() } }, ...prev]);
      setShowAddFill(false);
      setLitresAdded(''); setMachineId(''); setCost(''); setFillNote('');
      toast.success(`${litres.toLocaleString()}L added to ${tank.name}`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to log fill');
    }
    setSaving(false);
  };

  const formatDateTime = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString(language, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const pct = tank ? Math.round((tank.currentLitres / tank.capacityLitres) * 100) : 0;
  const threshold = tank?.lowLevelThreshold || 20;
  const colour = pct <= threshold ? 'var(--color-danger)' : pct <= threshold * 1.5 ? 'var(--color-warning)' : 'var(--color-success)';
  const isLow = pct <= threshold;

  const totalFillsThisMonth = fills.filter(f => {
    const d = f.timestamp?.toDate ? f.timestamp.toDate() : new Date(f.timestamp);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).reduce((sum, f) => sum + (f.litresAdded || 0), 0);

  if (loading) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', fontSize: 13, color: 'var(--color-text-tertiary)' }}>
        Loading...
      </div>
    </Layout>
  );

  if (!tank) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div onClick={() => navigate('/fuel')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Fuel
        </div>
        <div style={{ fontSize: 16, color: 'var(--color-text-secondary)' }}>Tank not found</div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 680, margin: '0 auto' }}>

        <div onClick={() => navigate('/fuel')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 20 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Fuel management
        </div>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>{tank.name}</h1>
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>{tank.siteName}</div>
          </div>
          <Button
            variant="primary"
            icon={<IconPlus size={14} stroke={1.5} />}
            onClick={() => setShowAddFill(true)}
          >
            Log fill
          </Button>
        </div>

        {/* Low level alert */}
        {isLow && (
          <div style={{ background: 'var(--color-danger-light)', border: '0.5px solid var(--color-danger)', borderRadius: 'var(--radius-lg)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <IconAlertTriangle size={16} color="var(--color-danger)" stroke={1.5} />
            <div style={{ fontSize: 13, color: 'var(--color-danger)' }}>
              Tank is below {threshold}% — order more derv
            </div>
          </div>
        )}

        {/* Tank level card */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 36, fontWeight: 400, color: colour, marginBottom: 2 }}>{pct}%</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                {tank.currentLitres?.toLocaleString()}L of {tank.capacityLitres?.toLocaleString()}L
              </div>
            </div>
            <div style={{ width: 48, height: 48, borderRadius: 'var(--radius-lg)', background: colour + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IconDroplet size={24} color={colour} stroke={1.5} />
            </div>
          </div>
          <div style={{ height: 8, background: 'var(--color-bg-tertiary)', borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
            <div style={{ height: '100%', width: `${pct}%`, background: colour, borderRadius: 4, transition: 'width 0.3s' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--color-text-tertiary)' }}>
            <span>0L</span>
            <span>Low level: {threshold}%</span>
            <span>{tank.capacityLitres?.toLocaleString()}L</span>
          </div>
        </Card>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 20 }}>
          {[
            { label: 'Total fills', value: fills.length, colour: 'var(--color-text-primary)' },
            { label: 'This month (L)', value: totalFillsThisMonth.toLocaleString(), colour: 'var(--color-text-primary)' },
            { label: 'Capacity (L)', value: tank.capacityLitres?.toLocaleString() || '—', colour: 'var(--color-text-primary)' },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '14px 16px' }}>
              <div style={{ fontSize: 24, fontWeight: 400, color: s.colour, marginBottom: 4 }}>{s.value}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Fill history */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 400 }}>Fill history</div>
        </div>

        <Card padding="0">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', padding: '8px 16px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)' }}>
            {['Date & time', 'Litres added', 'Machine', 'Cost'].map(h => (
              <div key={h} style={{ fontSize: 10, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>{h}</div>
            ))}
          </div>

          {fills.length === 0 ? (
            <EmptyState
              icon={<IconDroplet size={28} stroke={1.5} />}
              title="No fills recorded"
              message="Log your first fuel fill"
            />
          ) : fills.map((fill, i) => (
            <div
              key={fill.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr 1fr',
                padding: '12px 16px',
                borderBottom: i < fills.length - 1 ? '0.5px solid var(--color-border-light)' : 'none',
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ fontSize: 12, color: 'var(--color-text-primary)' }}>{formatDateTime(fill.timestamp)}</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 1 }}>{fill.addedByName}</div>
              </div>
              <div style={{ fontSize: 13, color: 'var(--color-success)', fontWeight: 400 }}>
                +{fill.litresAdded?.toLocaleString()}L
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                {fill.machineName || '—'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                {fill.cost ? `£${fill.cost.toLocaleString()}` : '—'}
              </div>
            </div>
          ))}
        </Card>

      </div>

      {/* Add fill modal */}
      <Modal
        open={showAddFill}
        onClose={() => setShowAddFill(false)}
        title={`Log fill — ${tank?.name}`}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowAddFill(false)}>Cancel</Button>
            <Button
              variant="primary"
              onClick={handleAddFill}
              disabled={saving}
              loading={saving}
              icon={<IconCheck size={14} stroke={1.5} />}
            >
              Confirm fill
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: 12, color: 'var(--color-text-secondary)' }}>
            Current level: <strong style={{ color: colour }}>{pct}% · {tank.currentLitres?.toLocaleString()}L</strong>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
              Litres added <span style={{ color: 'var(--color-danger)' }}>*</span>
            </label>
            <input
              type="number"
              placeholder="e.g. 500"
              value={litresAdded}
              onChange={e => setLitresAdded(e.target.value)}
              min={1}
              max={tank.capacityLitres - tank.currentLitres}
              style={{ width: '100%', boxSizing: 'border-box' }}
              autoFocus
            />
            {litresAdded && (
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                New level: {((tank.currentLitres || 0) + Number(litresAdded)).toLocaleString()}L · {Math.round(((tank.currentLitres + Number(litresAdded)) / tank.capacityLitres) * 100)}%
              </div>
            )}
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
              Machine filled <span style={{ color: 'var(--color-text-tertiary)' }}>— optional</span>
            </label>
            <select
              value={machineId}
              onChange={e => setMachineId(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box' }}
            >
              <option value="">Select machine</option>
              {machines.filter(m => m.siteId === tank.siteId).map(m => (
                <option key={m.id} value={m.id}>{m.makeLabel} {m.modelLabel}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
              Cost (£) <span style={{ color: 'var(--color-text-tertiary)' }}>— optional</span>
            </label>
            <input
              type="number"
              placeholder="e.g. 750.00"
              value={cost}
              onChange={e => setCost(e.target.value)}
              min={0}
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
              Note <span style={{ color: 'var(--color-text-tertiary)' }}>— optional</span>
            </label>
            <input
              type="text"
              placeholder="e.g. Emergency top-up"
              value={fillNote}
              onChange={e => setFillNote(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </div>
        </div>
      </Modal>
    </Layout>
  );
}